import { useCallback, useMemo } from 'react';

import { SubFolderDtoStatus, useActivateSubfolder, useDeactivateSubfolder } from '@/api/generated';

enum SubfolderStatusActions {
  ACTIVATE = 'ACTIVATE',
  DEACTIVATE = 'DEACTIVATE',
  RESUME = 'RESUME',
  HOLD = 'HOLD'
}

export const useSubfolderStatusActions = (options: {
  organizationSlug: string;
  subfolderSlug: string;
  currentStatus?: SubFolderDtoStatus;
  onChange: (newStatus: SubFolderDtoStatus) => void;
}) => {
  const { organizationSlug, subfolderSlug, currentStatus, onChange } = options;

  const { mutateAsync: activateSubfolder, isLoading: isActivateSubfolderLoading } = useActivateSubfolder();
  const { mutateAsync: deactivateSubfolder, isLoading: isDeactivateSubfolderLoading } = useDeactivateSubfolder();

  const isStatusLoading = isActivateSubfolderLoading || isDeactivateSubfolderLoading;

  const statusTransitions = useMemo(
    () => [
      {
        from: SubFolderDtoStatus.DRAFT,
        to: SubFolderDtoStatus.ACTIVE,
        name: SubfolderStatusActions.ACTIVATE,
        handler: () => activateSubfolder({ organizationSlug, subfolderSlug })
      },
      {
        from: SubFolderDtoStatus.INACTIVE,
        to: SubFolderDtoStatus.ACTIVE,
        name: SubfolderStatusActions.ACTIVATE,
        handler: () => activateSubfolder({ organizationSlug, subfolderSlug })
      },
      {
        from: SubFolderDtoStatus.ACTIVE,
        to: SubFolderDtoStatus.INACTIVE,
        name: SubfolderStatusActions.DEACTIVATE,
        handler: () => deactivateSubfolder({ organizationSlug, subfolderSlug })
      }
    ],
    [activateSubfolder, deactivateSubfolder, organizationSlug, subfolderSlug]
  );

  const availableTransitions = useMemo(
    () => statusTransitions.filter(({ from }) => from === currentStatus),
    [currentStatus, statusTransitions]
  );
  const availableStatuses = useMemo(
    () => [currentStatus, ...availableTransitions.map(({ to }) => to)],
    [availableTransitions, currentStatus]
  );
  const isFinalState = useMemo(() => !availableTransitions.length, [availableTransitions]);
  const availableActions = useMemo(() => availableTransitions.map(({ name }) => name), [availableTransitions]);

  const isActivateAvailable = useMemo(
    () => availableActions.includes(SubfolderStatusActions.ACTIVATE),
    [availableActions]
  );
  const isDeactivateAvailable = useMemo(
    () => availableActions.includes(SubfolderStatusActions.DEACTIVATE),
    [availableActions]
  );

  const handleStatusChange = useCallback(
    async (newStatus: SubFolderDtoStatus) => {
      const originalStatus = currentStatus;
      try {
        const transition = availableTransitions.find(({ from, to }) => originalStatus === from && newStatus === to);
        if (!transition) {
          throw new Error(`Transition impossible: ${originalStatus} => ${newStatus}`);
        }
        onChange(newStatus);
        if (transition.handler) {
          await transition.handler();
        } else {
          // eslint-disable-next-line no-console
          console.warn(`Transition is not implemented: ${transition.name}`);
        }
      } catch (e) {
        // TODO: proper logging
        console.error(e); // eslint-disable-line no-console
        onChange(originalStatus!);
      }
    },
    [availableTransitions, currentStatus, onChange]
  );

  const handleActivate = useCallback(() => {
    if (isActivateAvailable) {
      handleStatusChange(SubFolderDtoStatus.ACTIVE);
    }
  }, [handleStatusChange, isActivateAvailable]);

  const handleDeactivate = useCallback(() => {
    if (isDeactivateAvailable) {
      handleStatusChange(SubFolderDtoStatus.INACTIVE);
    }
  }, [handleStatusChange, isDeactivateAvailable]);

  return {
    isLoading: isStatusLoading,
    isFinalState,
    availableStatuses,
    activate: handleActivate,
    deactivate: handleDeactivate,
    change: handleStatusChange,
    isActivateAvailable,
    isDeactivateAvailable
  };
};
